import { Quote, Star } from "lucide-react";
import type { Testimonial } from "@/lib/types";
import { portrait } from "@/lib/images";
import { Avatar } from "@/components/ui/Avatar";
import { RevealItem } from "@/components/ui/Reveal";
import { cn } from "@/lib/utils";
import { embedUrl, isFile } from "@/components/home9/H9VoiceVideos";

export function TestimonialCard({
  testimonial,
  className,
}: {
  testimonial: Testimonial;
  className?: string;
}) {
  return (
    <RevealItem className={cn("flex h-full flex-col", className)}>
      <figure className="relative flex flex-1 flex-col rounded-[var(--radius-card)] border border-line bg-white p-6 shadow-soft">
        <Quote className="h-8 w-8 text-gold-500/40" />
        <div className="mt-2 flex gap-0.5" aria-label={`${testimonial.rating} out of 5`}>
          {Array.from({ length: 5 }).map((_, i) => (
            <Star
              key={i}
              className={cn(
                "h-4 w-4",
                i < testimonial.rating ? "fill-gold-400 text-gold-400" : "text-line"
              )}
            />
          ))}
        </div>
        <blockquote className="mt-3 flex-1 text-[15px] leading-relaxed text-ink">
          “{testimonial.quote}”
        </blockquote>
        <figcaption className="mt-5 flex items-center gap-3 border-t border-line pt-4">
          <Avatar name={testimonial.name} accent={testimonial.image} size="sm" photo={testimonial.photo ?? portrait(testimonial.name)} />
          <div>
            <div className="text-sm font-semibold text-ink">{testimonial.name}</div>
            <div className="text-xs text-ink-muted">
              {testimonial.role}
              {testimonial.company ? ` · ${testimonial.company}` : ""}
            </div>
          </div>
        </figcaption>
      </figure>
      {testimonial.video && (isFile(testimonial.video) || embedUrl(testimonial.video)) && (
        <div className="mt-3 aspect-video overflow-hidden rounded-xl bg-black">
          {isFile(testimonial.video) ? (
            <video src={testimonial.video} controls controlsList="nodownload noremoteplayback" disablePictureInPicture onContextMenu={(e) => e.preventDefault()} preload="metadata" className="h-full w-full" />
          ) : (
            <iframe
              src={embedUrl(testimonial.video) ?? undefined}
              title={`${testimonial.name} - video testimonial`}
              loading="lazy"
              allow="encrypted-media; picture-in-picture; fullscreen"
              className="h-full w-full border-0"
            />
          )}
        </div>
      )}
    </RevealItem>
  );
}
